/**
 * @fileoverview Comments Hook
 *
 * Manages poll comments with support for nested replies, likes, and real-time updates.
 *
 * @module hooks/useComments
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { mockComments, type MockComment } from '@/data/mockData';

/**
 * Check if a poll ID is a mock poll (for development).
 * Mock polls start with 'feed-' prefix.
 * @internal
 */
const isMockPoll = (pollId: string) => pollId.startsWith('feed-');

/**
 * Convert mock comment data to Comment interface.
 * @internal
 */
const convertMockToComment = (mock: MockComment): Comment => ({
  id: mock.id,
  poll_id: mock.poll_id,
  user_id: mock.user_id,
  content: mock.content,
  parent_id: mock.parent_id,
  likes_count: mock.likes_count,
  created_at: mock.created_at,
  updated_at: mock.updated_at,
  profile: mock.profile,
  isLikedByUser: mock.isLikedByUser || false,
  replies: mock.replies?.map(convertMockToComment),
});

/**
 * Represents a comment with optional nested replies.
 */
export interface Comment {
  /** Unique comment ID */
  id: string;
  /** ID of the poll this comment belongs to */
  poll_id: string;
  /** ID of the comment author */
  user_id: string;
  /** Comment text content */
  content: string;
  /** Parent comment ID for replies (null for top-level) */
  parent_id: string | null;
  /** Number of likes on this comment */
  likes_count: number;
  /** ISO timestamp of creation */
  created_at: string;
  /** ISO timestamp of last update */
  updated_at: string;
  /** Author's profile data */
  profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  /** Nested replies (built client-side) */
  replies?: Comment[];
  /** Whether current user has liked this comment */
  isLikedByUser?: boolean;
}

/**
 * Hook for managing comments on a poll.
 *
 * Features:
 * - Fetch comments with nested replies
 * - Add comments and replies
 * - Delete own comments
 * - Like/unlike comments
 * - Real-time updates via Supabase Realtime
 * - Handles mock polls for development
 *
 * Comments are fetched flat from DB, then built into a tree structure client-side.
 *
 * @param pollId - ID of the poll to get comments for
 * @returns {Object} Comments state and methods
 * @property {Comment[]} comments - Nested comment tree
 * @property {boolean} isLoading - True while fetching
 * @property {boolean} isSubmitting - True while adding comment
 * @property {Function} addComment - Add a new comment or reply
 * @property {Function} deleteComment - Delete own comment
 * @property {Function} toggleLike - Like/unlike a comment
 * @property {Function} refetch - Manually refresh comments
 * @property {number} commentCount - Total comments including replies
 *
 * @example
 * const { comments, addComment, isLoading } = useComments(pollId);
 *
 * // Add top-level comment
 * await addComment('Great poll!');
 *
 * // Add reply
 * await addComment('I agree!', parentCommentId);
 */
export function useComments(pollId: string) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Track optimistically added comment IDs to avoid duplicates from realtime
  const optimisticIdsRef = useRef<Set<string>>(new Set());
  // Track profiles cache for realtime updates
  const profilesCacheRef = useRef<Map<string, { display_name: string | null; avatar_url: string | null }>>(new Map());

  const fetchComments = useCallback(async () => {
    if (!pollId) return;
    
    setIsLoading(true);
    
    // Handle mock polls
    if (isMockPoll(pollId)) {
      const mockData = mockComments[pollId] || [];
      const converted = mockData.map(convertMockToComment);
      setComments(converted);
      setIsLoading(false);
      return;
    }
    
    try {
      const { data: commentsData, error: commentsError } = await supabase
        .from('poll_comments')
        .select('*')
        .eq('poll_id', pollId)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      if (commentsData && commentsData.length > 0) {
        // Fetch profiles for all comment authors
        const userIds = [...new Set(commentsData.map(c => c.user_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, display_name, avatar_url')
          .in('user_id', userIds);

        const profilesMap = new Map(
          profilesData?.map(p => [p.user_id, p]) || []
        );
        
        // Cache profiles for realtime updates
        profilesData?.forEach(p => {
          profilesCacheRef.current.set(p.user_id, { display_name: p.display_name, avatar_url: p.avatar_url });
        });

        // Fetch user's likes if logged in
        let userLikesSet = new Set<string>();
        if (user) {
          const commentIds = commentsData.map(c => c.id);
          const { data: likesData } = await supabase
            .from('comment_likes')
            .select('comment_id')
            .eq('user_id', user.id)
            .in('comment_id', commentIds);
          
          userLikesSet = new Set(likesData?.map(l => l.comment_id) || []);
        }

        // Map comments with profiles and like status
        const commentsWithProfiles = commentsData.map(comment => ({
          ...comment,
          profile: profilesMap.get(comment.user_id) || undefined,
          isLikedByUser: userLikesSet.has(comment.id)
        }));

        // Build nested structure
        const nestedComments = buildNestedComments(commentsWithProfiles);
        setComments(nestedComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  }, [pollId, user]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Real-time subscription for comments
  useEffect(() => {
    if (!pollId || isMockPoll(pollId)) return;

    const channel = supabase
      .channel(`poll_comments:${pollId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'poll_comments',
          filter: `poll_id=eq.${pollId}`,
        },
        async (payload) => {
          const newComment = payload.new as Comment;
          
          // Skip if this is an optimistic comment we already added
          if (optimisticIdsRef.current.has(newComment.id)) {
            optimisticIdsRef.current.delete(newComment.id);
            return;
          }

          // Fetch profile for the new comment author if not cached
          let profile = profilesCacheRef.current.get(newComment.user_id);
          if (!profile) {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('display_name, avatar_url')
              .eq('user_id', newComment.user_id)
              .maybeSingle();
            
            if (profileData) {
              profile = profileData;
              profilesCacheRef.current.set(newComment.user_id, profile);
            }
          }

          const commentWithProfile: Comment = {
            ...newComment,
            profile: profile || { display_name: null, avatar_url: null },
            isLikedByUser: false,
            replies: [],
          };

          setComments(prev => {
            if (newComment.parent_id) {
              return addReplyToComment(prev, newComment.parent_id, commentWithProfile);
            } else {
              // Check if comment already exists (avoid duplicates)
              const exists = prev.some(c => c.id === newComment.id);
              if (exists) return prev;
              return [...prev, commentWithProfile];
            }
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'poll_comments',
          filter: `poll_id=eq.${pollId}`,
        },
        (payload) => {
          const updatedComment = payload.new as Comment;
          
          setComments(prev => updateCommentInTree(prev, updatedComment.id, {
            content: updatedComment.content,
            likes_count: updatedComment.likes_count,
            updated_at: updatedComment.updated_at,
          }));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'poll_comments',
          filter: `poll_id=eq.${pollId}`,
        },
        (payload) => {
          const deletedComment = payload.old as { id: string };
          setComments(prev => removeComment(prev, deletedComment.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [pollId]);

  const addComment = async (content: string, parentId?: string) => {
    if (!user) {
      toast.error('Please sign in to comment');
      return false;
    }

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      toast.error('Comment cannot be empty');
      return false;
    }

    if (trimmedContent.length > 1000) {
      toast.error('Comment is too long (max 1000 characters)');
      return false;
    }

    // Mock polls - add comment locally only
    if (isMockPoll(pollId)) {
      // Fetch user's actual profile
      let userProfile = { display_name: 'You' as string | null, avatar_url: null as string | null };
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('display_name, avatar_url')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profileData) {
          userProfile = profileData;
        }
      } catch (e) {
        console.error('Error fetching profile:', e);
      }

      const newComment: Comment = {
        id: `temp-${Date.now()}`,
        poll_id: pollId,
        user_id: user.id,
        content: trimmedContent,
        parent_id: parentId || null,
        likes_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        profile: {
          display_name: userProfile.display_name || 'Anonymous',
          avatar_url: userProfile.avatar_url,
        },
        isLikedByUser: false,
        replies: [],
      };
      
      if (parentId) {
        setComments(prev => addReplyToComment(prev, parentId, newComment));
      } else {
        setComments(prev => [...prev, newComment]);
      }
      // Added silently
      return true;
    }

    setIsSubmitting(true);
    
    // 1. Use cached profile OR placeholder immediately - NO await before optimistic add
    const cachedProfile = profilesCacheRef.current.get(user.id);
    const immediateProfile = cachedProfile || { display_name: 'You', avatar_url: null };

    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticComment: Comment = {
      id: optimisticId,
      poll_id: pollId,
      user_id: user.id,
      content: trimmedContent,
      parent_id: parentId || null,
      likes_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      profile: immediateProfile,
      isLikedByUser: false,
      replies: [],
    };

    // 2. ADD OPTIMISTIC COMMENT IMMEDIATELY (no await before this)
    if (parentId) {
      setComments(prev => addReplyToComment(prev, parentId, optimisticComment));
    } else {
      setComments(prev => [...prev, optimisticComment]);
    }

    // 3. THEN do the database insert
    try {
      const { data, error } = await supabase
        .from('poll_comments')
        .insert({
          poll_id: pollId,
          user_id: user.id,
          content: trimmedContent,
          parent_id: parentId || null
        })
        .select()
        .single();

      if (error) throw error;

      // 4. Fetch profile only if not cached (background, non-blocking for UI)
      let userProfile = cachedProfile;
      if (!userProfile && data) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('display_name, avatar_url')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profileData) {
          userProfile = profileData;
          profilesCacheRef.current.set(user.id, userProfile);
        }
      }

      // 5. Replace optimistic with real comment
      if (data) {
        optimisticIdsRef.current.add(data.id);
        
        setComments(prev => {
          const withoutOptimistic = removeComment(prev, optimisticId);
          const realComment: Comment = {
            ...data,
            profile: userProfile || immediateProfile,
            isLikedByUser: false,
            replies: [],
          };
          
          if (data.parent_id) {
            return addReplyToComment(withoutOptimistic, data.parent_id, realComment);
          } else {
            return [...withoutOptimistic, realComment];
          }
        });
      }

      return true;
    } catch (error: any) {
      console.error('Error adding comment:', error?.message || error, { pollId, userId: user.id, parentId });
      // Rollback optimistic update
      setComments(prev => removeComment(prev, optimisticId));
      toast.error('Failed to add comment');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!user) return false;

    // Mock polls - remove comment locally only
    if (isMockPoll(pollId)) {
      setComments(prev => removeComment(prev, commentId));
      // Deleted silently
      return true;
    }

    // Optimistic delete
    let deletedComment: Comment | null = null;
    let parentId: string | null = null;
    
    setComments(prev => {
      // Find and store the comment before deletion for rollback
      const findComment = (comments: Comment[]): Comment | null => {
        for (const c of comments) {
          if (c.id === commentId) return c;
          if (c.replies) {
            const found = findComment(c.replies);
            if (found) {
              parentId = c.id;
              return found;
            }
          }
        }
        return null;
      };
      deletedComment = findComment(prev);
      return removeComment(prev, commentId);
    });

    try {
      const { error } = await supabase
        .from('poll_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Deleted silently
      return true;
    } catch (error) {
      console.error('Error deleting comment:', error);
      // Rollback
      if (deletedComment) {
        if (parentId) {
          setComments(prev => addReplyToComment(prev, parentId!, deletedComment!));
        } else {
          setComments(prev => [...prev, deletedComment!]);
        }
      }
      toast.error('Failed to delete comment');
      return false;
    }
  };

  const toggleLike = async (commentId: string, isCurrentlyLiked: boolean) => {
    if (!user) {
      toast.error('Please sign in to like comments');
      return false;
    }

    // Mock polls - toggle locally only
    if (isMockPoll(pollId)) {
      setComments(prev => updateCommentLike(prev, commentId, !isCurrentlyLiked));
      return true;
    }

    // Optimistic update
    setComments(prev => updateCommentLike(prev, commentId, !isCurrentlyLiked));

    try {
      if (isCurrentlyLiked) {
        // Unlike
        const { error } = await supabase
          .from('comment_likes')
          .delete()
          .eq('comment_id', commentId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Like
        const { error } = await supabase
          .from('comment_likes')
          .insert({
            comment_id: commentId,
            user_id: user.id
          });

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error toggling like:', error);
      // Rollback
      setComments(prev => updateCommentLike(prev, commentId, isCurrentlyLiked));
      toast.error('Failed to update like');
      return false;
    }
  };

  // Count all comments including replies
  const getTotalCount = (commentList: Comment[]): number => {
    return commentList.reduce((count, comment) => {
      return count + 1 + (comment.replies ? getTotalCount(comment.replies) : 0);
    }, 0);
  };

  // Get the latest top-level comment for preview
  const latestComment = comments.length > 0 
    ? comments[comments.length - 1] 
    : null;

  return {
    comments,
    isLoading,
    isSubmitting,
    addComment,
    deleteComment,
    toggleLike,
    refetch: fetchComments,
    commentCount: getTotalCount(comments),
    latestComment
  };
}

/**
 * Build a nested comment tree from flat comment array.
 * Uses parent_id to establish parent-child relationships.
 *
 * @param flatComments - Flat array of comments from database
 * @returns Nested array with replies as children
 * @internal
 */
function buildNestedComments(flatComments: Comment[]): Comment[] {
  const commentMap = new Map<string, Comment>();
  const rootComments: Comment[] = [];

  // First pass: create map of all comments
  flatComments.forEach(comment => {
    commentMap.set(comment.id, { ...comment, replies: [] });
  });

  // Second pass: build tree structure
  flatComments.forEach(comment => {
    const mappedComment = commentMap.get(comment.id)!;
    if (comment.parent_id && commentMap.has(comment.parent_id)) {
      const parent = commentMap.get(comment.parent_id)!;
      parent.replies = parent.replies || [];
      parent.replies.push(mappedComment);
    } else {
      rootComments.push(mappedComment);
    }
  });

  return rootComments;
}

/**
 * Update like status on a comment in nested structure.
 * Recursively searches through replies.
 *
 * @param comments - Current comment tree
 * @param commentId - ID of comment to update
 * @param isLiked - New like status
 * @returns Updated comment tree
 * @internal
 */
function updateCommentLike(comments: Comment[], commentId: string, isLiked: boolean): Comment[] {
  return comments.map(comment => {
    if (comment.id === commentId) {
      return {
        ...comment,
        isLikedByUser: isLiked,
        likes_count: isLiked ? comment.likes_count + 1 : Math.max(comment.likes_count - 1, 0)
      };
    }
    if (comment.replies && comment.replies.length > 0) {
      return {
        ...comment,
        replies: updateCommentLike(comment.replies, commentId, isLiked)
      };
    }
    return comment;
  });
}

/**
 * Update specific fields on a comment in nested structure.
 *
 * @param comments - Current comment tree
 * @param commentId - ID of comment to update
 * @param updates - Partial comment fields to update
 * @returns Updated comment tree
 * @internal
 */
function updateCommentInTree(comments: Comment[], commentId: string, updates: Partial<Comment>): Comment[] {
  return comments.map(comment => {
    if (comment.id === commentId) {
      return { ...comment, ...updates };
    }
    if (comment.replies && comment.replies.length > 0) {
      return {
        ...comment,
        replies: updateCommentInTree(comment.replies, commentId, updates)
      };
    }
    return comment;
  });
}

/**
 * Add a reply to a specific comment in nested structure.
 * Recursively searches for parent comment.
 *
 * @param comments - Current comment tree
 * @param parentId - ID of parent comment
 * @param newReply - New reply to add
 * @returns Updated comment tree with new reply
 * @internal
 */
function addReplyToComment(comments: Comment[], parentId: string, newReply: Comment): Comment[] {
  return comments.map(comment => {
    if (comment.id === parentId) {
      const existingReplies = comment.replies || [];
      // Deduplicate: skip if reply already exists
      if (existingReplies.some(r => r.id === newReply.id)) {
        return comment;
      }
      return {
        ...comment,
        replies: [...existingReplies, newReply]
      };
    }
    if (comment.replies && comment.replies.length > 0) {
      return {
        ...comment,
        replies: addReplyToComment(comment.replies, parentId, newReply)
      };
    }
    return comment;
  });
}

/**
 * Remove a comment from nested structure.
 * Recursively searches and removes from any level.
 *
 * @param comments - Current comment tree
 * @param commentId - ID of comment to remove
 * @returns Updated comment tree without the comment
 * @internal
 */
function removeComment(comments: Comment[], commentId: string): Comment[] {
  return comments
    .filter(comment => comment.id !== commentId)
    .map(comment => {
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: removeComment(comment.replies, commentId)
        };
      }
      return comment;
    });
}
